import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculerJoursOuvres } from '@/lib/heures-utils';

interface RetraitParams {
  affectationId: string;
  heuresDernierJour: number;
}

/**
 * Hook pour gérer le retrait d'un membre d'un chantier
 * Met à jour l'affectation avec la date de fin, les heures du dernier jour et recalcule jours_travailles
 */
export function useRetraitChantier() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const retirerDuChantier = async ({ affectationId, heuresDernierJour }: RetraitParams) => {
    setIsLoading(true);
    setError(null);

    try {
      const today = new Date().toISOString().split('T')[0];

      // 1. Récupérer l'affectation pour avoir la date de début
      const { data: affectation, error: fetchError } = await supabase
        .from('affectations_chantiers')
        .select('date_debut, heures_par_jour')
        .eq('id', affectationId)
        .single();

      if (fetchError) throw fetchError;
      if (!affectation) throw new Error('Affectation non trouvée');

      // 2. Calculer le nombre de jours ouvrés réels
      const joursOuvres = calculerJoursOuvres(affectation.date_debut, today);

      // 3. Mettre à jour l'affectation
      const { error: updateError } = await supabase
        .from('affectations_chantiers')
        .update({
          date_fin: today,
          jours_travailles: joursOuvres,
          heures_dernier_jour: heuresDernierJour,
        })
        .eq('id', affectationId);

      if (updateError) throw updateError;
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Erreur lors du retrait');
      setError(e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  return { retirerDuChantier, isLoading, error };
}
